import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationSchema from "_shared/_api/queries/pagination/_schema/pagination-parameters-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import ForumFilterParametersSchema from "messaging/forums/_api/queries/_schemas/forum-filter-schema";
import ForumOrderSchema from "messaging/forums/_api/queries/_schemas/forum-order-schema";
import ForumQuery, {
  ForumFilterParameters,
  ForumOrderField,
  ForumOrderParameters,
} from "messaging/forums/_api/queries/forum-query";
import ForumEntityClass from "notifications/message-notifications/forum-info/forum-entity-class";
import ForumName from "notifications/message-notifications/forum-info/forum-name";
import { z } from "zod";

export type ForumQueryJSON = z.infer<typeof ForumQuerySchema.ForumQueryJSONZ>;

export type ForumQueryParams = z.infer<typeof ForumQuerySchema.ForumQueryZ>;

export default class ForumQuerySchema {
  public static readonly ForumQueryJSONZ = z.object({
    filter: ForumFilterParametersSchema.ForumFilterParametersJSONZ,
    order: ForumOrderSchema.ForumOrderZ,
    pagination: PaginationSchema.PaginationZ.nullish(),
  });

  public static readonly ForumQueryZ = z.object({
    filter: ForumFilterParametersSchema.ForumFilterParametersZ,
    order: ForumOrderSchema.ForumOrderZ,
    pagination: PaginationSchema.PaginationZ,
  });

  public static validateJSON(values: unknown): ForumQueryJSON {
    return parseOrThrow(ForumQuerySchema.ForumQueryJSONZ, values, {
      logError: true,
      schemaName: "ForumQuerySchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is ForumQueryJSON {
    try {
      ForumQuerySchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: ForumQueryJSON): ForumQuery {
    const parsed = ForumQuerySchema.validateJSON(json);

    const filterParameters = new ForumFilterParameters({
      name: ForumName.fromJSON(parsed.filter.name) ?? undefined,
      entityId: Guid.fromJSON(parsed.filter.entityId) ?? undefined,
      entityClass: ForumEntityClass.fromJSON(parsed.filter.entityClass) ?? undefined,
      context: parsed.filter.context ?? undefined,
      subscriberId: Guid.fromJSON(parsed.filter.subscriberId) ?? undefined,
      minCreatedDate: AHDate.fromJSON(parsed.filter.minCreatedDate) ?? undefined,
      maxCreatedDate: AHDate.fromJSON(parsed.filter.maxCreatedDate) ?? undefined,
      systemOnly: AHBoolean.fromJSON(parsed.filter.systemOnly) ?? undefined,
      forumId: Guid.fromJSON(parsed.filter.forumId) ?? undefined,
      query: parsed.filter.query ?? undefined,
      unread: parsed.filter.unread ?? undefined,
    });

    const orderParameters = parsed.order
      ? new ForumOrderParameters(parsed.order)
      : new ForumOrderParameters({
          orderBy: ForumOrderField.CreatedDate,
          orderDir: OrderDirection.Descending,
        });

    const paginationParameters = parsed.pagination
      ? PaginationParameters.create({
          pageIndex: parsed.pagination.pageIndex,
          pageSize: parsed.pagination.pageSize,
        })
      : undefined;

    return new ForumQuery({ filterParameters: filterParameters, orderParameters, paginationParameters });
  }

  public static serialize(query: ForumQuery): ForumQueryJSON {
    const { filterParameters, orderParameters, paginationParameters } = query;

    const json: ForumQueryJSON = {
      filter: {
        name: filterParameters.name?.toJSON(),
        minCreatedDate: filterParameters.minCreatedDate?.toJSON(),
        maxCreatedDate: filterParameters.maxCreatedDate?.toJSON(),
        query: filterParameters.query,
        entityClass: filterParameters.entityClass?.toJSON(),
        entityId: filterParameters.entityId?.toJSON(),
        context: filterParameters.context,
        systemOnly: filterParameters.systemOnly?.toJSON(),
        unread: filterParameters.unread,
      },
      order: orderParameters
        ? {
            orderBy: orderParameters.orderBy,
            orderDir: orderParameters.orderDir,
          }
        : undefined,
      pagination: paginationParameters
        ? {
            pageIndex: paginationParameters.pageIndex,
            pageSize: paginationParameters.pageSize,
          }
        : undefined,
    };

    return parseOrThrow(ForumQuerySchema.ForumQueryJSONZ, json, {
      logError: true,
      schemaName: "ForumQuerySchema",
      operation: "serialize",
      payloadSample: json,
    });
  }
}
